import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  FolderPlusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  priceBookService,
  PriceBookCategory,
  PriceBookItem,
  QuotePolicy,
  QuotePolicyModel,
} from '../services/priceBookService';
import {
  PageHeader,
  Button,
  FormField,
  TextInput,
  TextArea,
  Select,
  Badge,
  DataTable,
  DataTableColumn,
  RowAction,
} from '../components/ui';

const emptyItem: Partial<PriceBookItem> = {
  name: '',
  description: '',
  pricingModel: 'FIXED',
  unit: 'job',
  unitPrice: 0,
  minPrice: null,
  maxPrice: null,
  laborHours: null,
  vatApplies: true,
  active: true,
  categoryId: null,
};

const PriceBook: React.FC = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editing, setEditing] = useState<Partial<PriceBookItem> | null>(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ['price-book', 'categories'],
    queryFn: () => priceBookService.listCategories(),
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['price-book', 'items', { search }],
    queryFn: () => priceBookService.listItems({ includeInactive: true, search }),
  });

  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return items;
    if (categoryFilter === 'uncategorised') return items.filter((i) => !i.categoryId);
    return items.filter((i) => i.categoryId === categoryFilter);
  }, [items, categoryFilter]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['price-book'] });
  };

  const onDelete = async (row: PriceBookItem) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    await priceBookService.deleteItem(row.id);
    refresh();
  };

  const columns: DataTableColumn<PriceBookItem>[] = [
    {
      id: 'name',
      header: 'Item',
      render: (row) => (
        <div>
          <div className="text-sm font-semibold text-slate-900">{row.name}</div>
          {row.sku && (
            <div className="text-xs text-slate-500">SKU {row.sku}</div>
          )}
          {row.description && (
            <div className="text-xs text-slate-500 line-clamp-1">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      render: (row) =>
        row.category?.name ? (
          <Badge tone="neutral">{row.category.name}</Badge>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
    {
      id: 'pricing',
      header: 'Pricing',
      render: (row) => (
        <div className="text-sm">
          <div className="text-slate-900">
            £{row.unitPrice.toFixed(2)}{' '}
            <span className="text-xs text-slate-500">/ {row.unit}</span>
          </div>
          <div className="text-[11px] text-slate-500">
            {row.pricingModel}
            {row.pricingModel === 'RANGE' &&
              (row.minPrice != null || row.maxPrice != null) &&
              ` · £${row.minPrice ?? 0} – £${row.maxPrice ?? '∞'}`}
          </div>
        </div>
      ),
    },
    {
      id: 'vat',
      header: 'VAT',
      render: (row) =>
        row.vatApplies ? (
          <Badge tone="info">Incl. VAT</Badge>
        ) : (
          <Badge tone="neutral">No VAT</Badge>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) =>
        row.active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
  ];

  const rowActions = (row: PriceBookItem): RowAction[] => [
    {
      id: 'edit',
      label: 'Edit',
      onClick: () => setEditing(row),
      icon: PencilSquareIcon,
    },
    {
      id: 'toggle',
      label: row.active ? 'Deactivate' : 'Reactivate',
      onClick: async () => {
        await priceBookService.updateItem(row.id, { active: !row.active });
        refresh();
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      danger: true,
      onClick: () => onDelete(row),
      icon: TrashIcon,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Price book & quote policy"
        subtitle="The catalogue your engineers use to build quick quotes on site. Categorise, set prices, and control who can approve quotes."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<FolderPlusIcon className="w-4 h-4" />}
              onClick={() => setCategoryModalOpen(true)}
            >
              Manage categories
            </Button>
            <Button
              size="sm"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={() => setEditing({ ...emptyItem })}
            >
              New item
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,240px,auto] gap-3 items-end">
        <FormField label="Search">
          <TextInput
            placeholder="Name, description or SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FormField>
        <FormField label="Category">
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">All categories</option>
            <option value="uncategorised">Uncategorised</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </FormField>
        <div className="text-xs text-slate-500 pb-2 flex items-center gap-1">
          <Squares2X2Icon className="w-4 h-4" /> {filtered.length} item{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        getRowKey={(r) => r.id}
        rowActions={rowActions}
        loading={isLoading}
        empty={
          <div className="ui-table-empty-default">
            No price book items yet — create one to get started.
          </div>
        }
      />

      <QuotePolicyCard />


      {editing && (
        <ItemEditor
          item={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {categoryModalOpen && (
        <CategoryManager
          categories={categories}
          onClose={() => setCategoryModalOpen(false)}
          onChanged={refresh}
        />
      )}
    </div>
  );
};

const ItemEditor: React.FC<{
  item: Partial<PriceBookItem>;
  categories: PriceBookCategory[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ item, categories, onClose, onSaved }) => {
  const [form, setForm] = useState<Partial<PriceBookItem>>(item);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof PriceBookItem>(k: K, v: PriceBookItem[K] | null) =>
    setForm((f) => ({ ...f, [k]: v as any }));

  const submit = async () => {
    if (!form.name?.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Partial<PriceBookItem> = {
        ...form,
        unitPrice: Number(form.unitPrice || 0),
        minPrice: form.minPrice != null ? Number(form.minPrice) : null,
        maxPrice: form.maxPrice != null ? Number(form.maxPrice) : null,
        laborHours: form.laborHours != null ? Number(form.laborHours) : null,
      };
      if (form.id) {
        await priceBookService.updateItem(form.id, payload);
      } else {
        await priceBookService.createItem(payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold">{form.id ? 'Edit item' : 'New price book item'}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label="Name" required>
              <TextInput
                value={form.name || ''}
                onChange={(e) => set('name', e.target.value)}
              />
            </FormField>
            <FormField label="SKU (optional)">
              <TextInput
                value={form.sku || ''}
                onChange={(e) => set('sku', e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Description">
            <TextArea
              rows={2}
              value={form.description || ''}
              onChange={(e) => set('description', e.target.value)}
            />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label="Category">
              <Select
                value={form.categoryId || ''}
                onChange={(e) => set('categoryId', e.target.value || null)}
              >
                <option value="">Uncategorised</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Unit">
              <Select value={form.unit || 'job'} onChange={(e) => set('unit', e.target.value)}>
                <option value="job">per job</option>
                <option value="hour">per hour</option>
                <option value="each">each</option>
                <option value="m">per metre</option>
                <option value="m2">per sq. metre</option>
              </Select>
            </FormField>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <FormField label="Pricing model">
              <Select
                value={form.pricingModel || 'FIXED'}
                onChange={(e) => set('pricingModel', e.target.value as any)}
              >
                <option value="FIXED">Fixed</option>
                <option value="HOURLY">Hourly</option>
                <option value="RANGE">Range</option>
              </Select>
            </FormField>
            <FormField label="Unit price (£)">
              <TextInput
                type="number"
                step="0.01"
                min={0}
                value={form.unitPrice ?? 0}
                onChange={(e) => set('unitPrice', parseFloat(e.target.value) || 0)}
              />
            </FormField>
            <FormField label="Labour hours (optional)">
              <TextInput
                type="number"
                step="0.25"
                min={0}
                value={form.laborHours ?? ''}
                onChange={(e) =>
                  set('laborHours', e.target.value ? parseFloat(e.target.value) : null)
                }
              />
            </FormField>
          </div>
          {form.pricingModel === 'RANGE' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <FormField label="Min price (£)">
                <TextInput
                  type="number"
                  step="0.01"
                  value={form.minPrice ?? ''}
                  onChange={(e) =>
                    set('minPrice', e.target.value ? parseFloat(e.target.value) : null)
                  }
                />
              </FormField>
              <FormField label="Max price (£)">
                <TextInput
                  type="number"
                  step="0.01"
                  value={form.maxPrice ?? ''}
                  onChange={(e) =>
                    set('maxPrice', e.target.value ? parseFloat(e.target.value) : null)
                  }
                />
              </FormField>
            </div>
          )}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.vatApplies !== false}
                onChange={(e) => set('vatApplies', e.target.checked)}
              />
              VAT applies
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.active !== false}
                onChange={(e) => set('active', e.target.checked)}
              />
              Active
            </label>
          </div>
          <FormField label="Internal notes">
            <TextArea
              rows={2}
              value={form.notes || ''}
              onChange={(e) => set('notes', e.target.value)}
            />
          </FormField>
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {form.id ? 'Save changes' : 'Create item'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CategoryManager: React.FC<{
  categories: PriceBookCategory[];
  onClose: () => void;
  onChanged: () => void;
}> = ({ categories, onClose, onChanged }) => {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(Object.fromEntries(categories.map((c) => [c.id, c.name])));
  }, [categories]);

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await priceBookService.createCategory({ name: name.trim() });
      setName('');
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const rename = async (id: string) => {
    const next = editing[id]?.trim();
    const original = categories.find((c) => c.id === id)?.name;
    if (!next || next === original) return;
    try {
      await priceBookService.updateCategory(id, { name: next });
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Failed');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this category? Items in it will become uncategorised.')) return;
    try {
      await priceBookService.deleteCategory(id);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold">Manage categories</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          <div className="flex items-end gap-2">
            <FormField label="Add category" className="flex-1">
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Gas"
              />
            </FormField>
            <Button onClick={add} loading={saving} leftIcon={<PlusIcon className="w-4 h-4" />}>
              Add
            </Button>
          </div>

          {categories.length === 0 && (
            <div className="text-xs text-slate-500 py-2">No categories yet.</div>
          )}
          <ul className="divide-y divide-slate-100">
            {categories.map((c) => (
              <li key={c.id} className="flex items-center gap-2 py-2">
                <TextInput
                  value={editing[c.id] ?? c.name}
                  onChange={(e) => setEditing({ ...editing, [c.id]: e.target.value })}
                  onBlur={() => rename(c.id)}
                />
                <button
                  type="button"
                  className="p-2 text-slate-400 hover:text-red-600"
                  onClick={() => remove(c.id)}
                  aria-label="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>

          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
      </div>
    </div>
  );
};

const QuotePolicyCard: React.FC = () => {
  const qc = useQueryClient();
  const { data: policy, isLoading } = useQuery({
    queryKey: ['price-book', 'policy'],
    queryFn: () => priceBookService.getPolicy(),
  });

  const [form, setForm] = useState<Partial<QuotePolicy>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (policy) setForm(policy);
  }, [policy]);

  const set = <K extends keyof QuotePolicy>(k: K, v: QuotePolicy[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await priceBookService.updatePolicy({
        model: form.model,
        thresholdAmount:
          form.model === 'THRESHOLD'
            ? form.thresholdAmount != null
              ? Number(form.thresholdAmount)
              : null
            : null,
        maxDiscountPct: Number(form.maxDiscountPct ?? 0),
        validDays: Number(form.validDays ?? 14),
        allowCustomItems: !!form.allowCustomItems,
        requireCustomerSignature: !!form.requireCustomerSignature,
        autoConvertOnAcceptance: !!form.autoConvertOnAcceptance,
        notifyAdminsOnSubmit: !!form.notifyAdminsOnSubmit,
      });
      qc.invalidateQueries({ queryKey: ['price-book', 'policy'] });
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  if (isLoading && !policy) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading quote policy…
      </div>
    );
  }

  const modelOptions: { value: QuotePolicyModel; title: string; desc: string }[] = [
    {
      value: 'THRESHOLD',
      title: 'Threshold',
      desc: 'Engineers auto-approve quotes up to a set amount; above that, admin approval required.',
    },
    {
      value: 'ALWAYS_ADMIN',
      title: 'Always admin',
      desc: 'Every engineer-generated quote needs admin approval before it is sent to the customer.',
    },
    {
      value: 'TRUST',
      title: 'Trust',
      desc: 'Engineers self-approve all their quotes. Admins monitor via the dashboard.',
    },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="px-6 py-5 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-900">Quote policy</h2>
        <p className="text-sm text-slate-500 mt-1">
          How on-site quotes from your engineers are approved and sent.
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid gap-3 md:grid-cols-3">
          {modelOptions.map((opt) => {
            const selected = form.model === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => set('model', opt.value)}
                className={[
                  'text-left rounded-lg border p-4 transition',
                  selected
                    ? 'border-slate-900 ring-1 ring-slate-900 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white',
                ].join(' ')}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{opt.title}</div>
                  {selected && <Badge tone="success">Selected</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{opt.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {form.model === 'THRESHOLD' && (
            <FormField label="Auto-approve threshold (£)">
              <TextInput
                type="number"
                step="0.01"
                min={0}
                value={form.thresholdAmount ?? ''}
                onChange={(e) =>
                  set(
                    'thresholdAmount',
                    (e.target.value ? parseFloat(e.target.value) : null) as any,
                  )
                }
                placeholder="e.g. 500"
              />
            </FormField>
          )}
          <FormField label="Max discount engineers can apply (%)">
            <TextInput
              type="number"
              step="1"
              min={0}
              max={100}
              value={form.maxDiscountPct ?? 0}
              onChange={(e) => set('maxDiscountPct', parseFloat(e.target.value) || 0)}
            />
          </FormField>
          <FormField label="Quote valid for (days)">
            <TextInput
              type="number"
              step="1"
              min={1}
              value={form.validDays ?? 14}
              onChange={(e) => set('validDays', parseInt(e.target.value) || 14)}
            />
          </FormField>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!form.allowCustomItems}
              onChange={(e) => set('allowCustomItems', e.target.checked)}
            />
            <span>
              <span className="font-medium block">Allow custom items</span>
              <span className="text-xs text-slate-500">
                Engineers can add one-off items not on the price book.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!form.requireCustomerSignature}
              onChange={(e) => set('requireCustomerSignature', e.target.checked)}
            />
            <span>
              <span className="font-medium block">Require customer signature</span>
              <span className="text-xs text-slate-500">
                Capture a signature on the engineer's device before the quote is accepted.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!form.autoConvertOnAcceptance}
              onChange={(e) => set('autoConvertOnAcceptance', e.target.checked)}
            />
            <span>
              <span className="font-medium block">Auto-convert accepted quotes into jobs</span>
              <span className="text-xs text-slate-500">
                As soon as the customer signs, a matching job is created.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.notifyAdminsOnSubmit !== false}
              onChange={(e) => set('notifyAdminsOnSubmit', e.target.checked)}
            />
            <span>
              <span className="font-medium block">Notify admins on every submission</span>
              <span className="text-xs text-slate-500">
                Send an in-app notification to admins whenever an engineer submits a quote.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          {savedAt && !saving && (
            <span className="text-xs text-emerald-600">Saved</span>
          )}
          <Button onClick={save} loading={saving}>
            Save policy
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PriceBook;
